/*
 * Valenor — Spec values FR→EN at render time.
 *
 * Targets `[data-vln-specs-translate][data-locale="en"]` (the wrapper
 * around the remontoir/boîte tech-specs rows). Walks the `.vln-spec-row__v`
 * text nodes and applies regex-based replacements for the recurring
 * vocabulary used in the metaobject values.
 *
 * Why JS rather than a Liquid case/when: the metaobject definition isn't
 * marked translatable in the Shopify Translations API, so we can't push
 * EN values via translationsRegister. A pre-baked Liquid lookup of 600+
 * unique strings was too brittle (rule chaining produced garbled output).
 * Rule-based JS keeps the source values intact and only displays a
 * translated render to EN shoppers.
 *
 * Order matters in REPLACEMENTS: longer phrases first so they match
 * before single words.
 */
(() => {
  if (typeof window === 'undefined' || window.__vlnSpecsI18nInit) return;
  window.__vlnSpecsI18nInit = true;

  // Unicode-aware word boundaries: \b in JS doesn't anchor on accented
  // characters (é, à, ç, etc.), so we use lookarounds against the Latin
  // letter range. WBL = "word-boundary left", WBR = "word-boundary right".
  const WBL = '(?<![A-Za-zÀ-ÿ])';
  const WBR = '(?![A-Za-zÀ-ÿ])';
  const ACC = (pat) => new RegExp(WBL + pat + WBR, 'g');
  const ACCi = (pat) => new RegExp(WBL + pat + WBR, 'gi');

  // Longer phrases first to win against single-word rules below.
  const REPLACEMENTS = [
    // composite phrases
    [/Moteur Mabuchi silencieux/g, 'Silent Mabuchi motor'],
    [/Moteur Marlboro silencieux/g, 'Silent Marlboro motor'],
    [/Moteur Mabuchi/g, 'Mabuchi motor'],
    [/Moteur Marlboro/g, 'Marlboro motor'],
    [/Mabuchi silencieux/g, 'Silent Mabuchi'],
    [/Marlboro silencieux/g, 'Silent Marlboro'],
    [/annoncé silencieux/g, 'silent-rated'],
    [/fonctionnement silencieux/g, 'silent operation'],
    [/avec mode silencieux/g, 'with silent mode'],
    [/avec télécommande/g, 'remote control included'],
    [/télécommande incluse/g, 'remote control included'],
    [/haute qualité/g, 'premium grade'],
    [/ultra-silencieux/g, 'ultra-silent'],

    // power / cable / batteries
    [/câble USB/g, 'USB cable'],
    [/piles AAA/g, 'AAA batteries'],
    [/piles AA/g, 'AA batteries'],
    [/Alimentation secteur/g, 'Mains power'],
    [/alimentation secteur/g, 'mains power'],
    [/Adaptateur EU\/UK\/US\/CA inclus/g, 'EU/UK/US/CA adapters included'],
    [/adaptateur EU\/UK\/US\/CA inclus/g, 'EU/UK/US/CA adapters included'],
    [/Adaptateur EU inclus/g, 'EU adapter included'],
    [/adaptateur EU inclus/g, 'EU adapter included'],
    [/Batterie au lithium intégrée/g, 'Built-in lithium battery'],
    [/batterie au lithium intégrée/g, 'built-in lithium battery'],
    [/compartiment externe pour piles/g, 'external battery compartment'],
    [/compartiment interne pour piles/g, 'internal battery compartment'],
    [/Secteur ou piles/g, 'Mains or batteries'],
    [/secteur ou piles/g, 'mains or batteries'],
    [/secteur ou par piles/g, 'mains or batteries'],
    [/secteur uniquement/g, 'mains only'],
    [/Secteur uniquement/g, 'Mains only'],
    [/(^|[\s\-,;·]) ?secteur(?=[\s\-,;·.]|$)/g, '$1mains'],
    [/(^|[\s\-,;·]) ?Secteur(?=[\s\-,;·.]|$)/g, '$1Mains'],
    [/Adaptateur secteur/g, 'Mains adapter'],
    [/adaptateur secteur/g, 'mains adapter'],

    // rotation directions (longer specific phrases first)
    [/Horaire, anti-horaire,? ou alterné(e?)/gi, 'Clockwise, counter-clockwise or alternating'],
    [/Horaire, antihoraire,? ou alterné(e?)/gi, 'Clockwise, counter-clockwise or alternating'],
    [/Horaire, anti-horaire,? alterné(e?)/gi, 'Clockwise, counter-clockwise, alternating'],
    [/Horaire, antihoraire,? alterné(e?)/gi, 'Clockwise, counter-clockwise, alternating'],
    [/Horaire, anti-horaire,? ou bidirectionnel/gi, 'Clockwise, counter-clockwise or bidirectional'],
    [/Horaire, antihoraire,? ou bidirectionnel/gi, 'Clockwise, counter-clockwise or bidirectional'],
    [/Horaire, anti-horaire,? bidirectionnel/gi, 'Clockwise, counter-clockwise, bidirectional'],
    [/Horaire, antihoraire,? bidirectionnel/gi, 'Clockwise, counter-clockwise, bidirectional'],
    [/Horaire, anti-horaire,? ou combinée?/gi, 'Clockwise, counter-clockwise or combined'],
    [/Horaire, antihoraire,? ou combinée?/gi, 'Clockwise, counter-clockwise or combined'],
    [/Horaire, anti-horaire,? combinée?/gi, 'Clockwise, counter-clockwise, combined'],
    [/Horaire, antihoraire,? combinée?/gi, 'Clockwise, counter-clockwise, combined'],
    [/Horaire, anti-horaire,? ou mixte/gi, 'Clockwise, counter-clockwise or mixed'],
    [/Horaire, antihoraire,? ou mixte/gi, 'Clockwise, counter-clockwise or mixed'],
    [/Horaire, anti-horaire,? mixte/gi, 'Clockwise, counter-clockwise, mixed'],
    [/Horaire, antihoraire,? mixte/gi, 'Clockwise, counter-clockwise, mixed'],
    [/Horaire et anti-horaire/gi, 'Clockwise and counter-clockwise'],
    [/Horaire et antihoraire/gi, 'Clockwise and counter-clockwise'],
    [/Horaire ou anti-horaire/gi, 'Clockwise or counter-clockwise'],
    [/Horaire ou antihoraire/gi, 'Clockwise or counter-clockwise'],
    [/Sens horaire/g, 'Clockwise'],
    [/sens horaire/g, 'clockwise'],
    [/Sens antihoraire/g, 'Counter-clockwise'],
    [/sens antihoraire/g, 'counter-clockwise'],
    [/anti-horaire/g, 'counter-clockwise'],
    [/antihoraire/g, 'counter-clockwise'],
    [/(\b)horaire(\b)/g, '$1clockwise$2'],
    [/Horaire(\b)/g, 'Clockwise$1'],
    [/(\b)alternance(\b)/g, '$1alternating$2'],
    [/(\b)alternées?(\b)/g, '$1alternating$2'],
    [/(\b)alternés?(\b)/g, '$1alternating$2'],
    [/(\b)alterné(\b)/g, '$1alternating$2'],
    [/Bidirectionnel/g, 'Bidirectional'],
    [/bidirectionnel/g, 'bidirectional'],

    // Modes / programmable (do NOT collapse plural — 5 modes stays 5 modes)
    [/avec mode silencieux/g, 'with silent mode'],
    [/programmables?/gi, 'programmable'],
    [/(\b)\d+\s+modes?(\b)/g, (m) => m], // identity to anchor plural awareness
    [/sens multiples/gi, 'multiple directions'],
    [/(\b)mode silencieux(\b)/g, '$1silent mode$2'],

    // counts (montres / coussins / clés / emplacements / rangements)
    [/(\d+)\s+montres? automatiques/gi, '$1 automatic watches'],
    [/(\d+)\s+montres? automatique/gi, '$1 automatic watch'],
    [/(\d+)\s+montres? en rotation/gi, '$1 watches in rotation'],
    [/(\d+)\s+montres en remontage/gi, '$1 watches winding'],
    [/(\d+)\s+montres? de stockage/gi, '$1 watches in storage'],
    [/(\d+)\s+montres/gi, '$1 watches'],
    [/(\d+)\s+montre(?!s)/gi, '$1 watch'],
    [/(\d+)\s+coussins flexibles/gi, '$1 flexible cushions'],
    [/(\d+)\s+coussins?/gi, (m, n) => n + (parseInt(n,10)===1?' cushion':' cushions')],
    [/(\d+)\s+clés?/gi, (m, n) => n + (parseInt(n,10)===1?' key':' keys')],
    [/(\d+)\s+emplacements?/gi, (m, n) => n + (parseInt(n,10)===1?' slot':' slots')],
    [/(\d+)\s+rangements?/gi, (m, n) => n + (parseInt(n,10)===1?' storage space':' storage spaces')],
    [/(\d+)\s+positions?/gi, (m, n) => n + (parseInt(n,10)===1?' position':' positions')],

    // common nouns
    [/Capacité active/g, 'Active capacity'],
    [/(\b)capacité(\b)/g, '$1capacity$2'],
    [/(\b)Capacité(\b)/g, '$1Capacity$2'],
    [/(\b)autonomie(\b)/g, '$1autonomy$2'],
    [/(\b)réserve de marche(\b)/g, '$1power reserve$2'],

    // materials
    [/(\b)acier inoxydable(\b)/g, '$1stainless steel$2'],
    [/(\b)Acier inoxydable(\b)/g, '$1Stainless steel$2'],
    [/(\b)acier(\b)/g, '$1steel$2'],
    [/(\b)Acier(\b)/g, '$1Steel$2'],
    [/(\b)fibre de carbone(\b)/g, '$1carbon fibre$2'],
    [/(\b)Fibre de carbone(\b)/g, '$1Carbon fibre$2'],
    [/(\b)bois massif(\b)/g, '$1solid wood$2'],
    [/(\b)Bois massif(\b)/g, '$1Solid wood$2'],
    [/(\b)bois de chêne(\b)/g, '$1oak$2'],
    [/(\b)Bois de chêne(\b)/g, '$1Oak$2'],
    [/(\b)bois de noyer(\b)/g, '$1walnut$2'],
    [/(\b)Bois de noyer(\b)/g, '$1Walnut$2'],
    [/(\b)bois de bambou(\b)/g, '$1bamboo$2'],
    [/(\b)Bois de bambou(\b)/g, '$1Bamboo$2'],
    [/(\b)bois de teck(\b)/g, '$1teak$2'],
    [/(\b)Bois de teck(\b)/g, '$1Teak$2'],
    [/(\b)bois de pin(\b)/g, '$1pinewood$2'],
    [/(\b)Bois de pin(\b)/g, '$1Pinewood$2'],
    [/(\b)bois de poirier(\b)/g, '$1pearwood$2'],
    [/(\b)Bois de poirier(\b)/g, '$1Pearwood$2'],
    [/(\b)bois de sapin(\b)/g, '$1fir$2'],
    [/(\b)Bois de sapin(\b)/g, '$1Fir$2'],
    [/(\b)merisier(\b)/g, '$1cherry$2'],
    [/(\b)Merisier(\b)/g, '$1Cherry$2'],
    [/(\b)bambou(\b)/g, '$1bamboo$2'],
    [/(\b)Bambou(\b)/g, '$1Bamboo$2'],
    [/(\b)chêne(\b)/g, '$1oak$2'],
    [/(\b)Chêne(\b)/g, '$1Oak$2'],
    [/(\b)noyer(\b)/g, '$1walnut$2'],
    [/(\b)Noyer(\b)/g, '$1Walnut$2'],
    [/(\b)bois(\b)/g, '$1wood$2'],
    [/(\b)Bois(\b)/g, '$1Wood$2'],
    [/(\b)cuir véritable(\b)/g, '$1genuine leather$2'],
    [/(\b)Cuir véritable(\b)/g, '$1Genuine leather$2'],
    [/(\b)cuir vegan(\b)/g, '$1vegan leather$2'],
    [/(\b)Cuir vegan(\b)/g, '$1Vegan leather$2'],
    [/(\b)cuir PU(\b)/g, '$1PU leather$2'],
    [/(\b)Cuir PU(\b)/g, '$1PU leather$2'],
    [/(\b)cuir skaï(\b)/g, '$1synthetic leather$2'],
    [/(\b)Cuir skaï(\b)/g, '$1Synthetic leather$2'],
    [/(\b)skaï(\b)/g, '$1synthetic leather$2'],
    [/(\b)Skaï(\b)/g, '$1Synthetic leather$2'],
    [/(\b)cuir(\b)/g, '$1leather$2'],
    [/(\b)Cuir(\b)/g, '$1Leather$2'],
    [/(\b)velours microfibre(\b)/g, '$1microfibre velvet$2'],
    [/(\b)Velours microfibre(\b)/g, '$1Microfibre velvet$2'],
    [/(\b)velours(\b)/g, '$1velvet$2'],
    [/(\b)Velours(\b)/g, '$1Velvet$2'],
    [/(\b)flanelle(\b)/g, '$1flannel$2'],
    [/(\b)Flanelle(\b)/g, '$1Flannel$2'],
    [/imitation suède/gi, 'faux suede'],
    [/imitation suede/gi, 'faux suede'],
    [/(\b)daim(\b)/g, '$1suede$2'],
    [/(\b)Daim(\b)/g, '$1Suede$2'],
    [/(\b)plastique(\b)/g, '$1plastic$2'],
    [/(\b)Plastique(\b)/g, '$1Plastic$2'],
    [/(\b)mousse haute densité(\b)/g, '$1high-density foam$2'],
    [/(\b)Mousse haute densité(\b)/g, '$1High-density foam$2'],
    [/(\b)mousse à mémoire de forme(\b)/g, '$1memory foam$2'],
    [/(\b)Mousse à mémoire de forme(\b)/g, '$1Memory foam$2'],
    [/(\b)mousse synthétique(\b)/g, '$1synthetic foam$2'],
    [/(\b)Mousse synthétique(\b)/g, '$1Synthetic foam$2'],
    [/(\b)mousse(\b)/g, '$1foam$2'],
    [/(\b)Mousse(\b)/g, '$1Foam$2'],
    [/(\b)verre(\b)/g, '$1glass$2'],
    [/(\b)Verre(\b)/g, '$1Glass$2'],
    [/(\b)vitre(\b)/g, '$1glass pane$2'],
    [/(\b)Vitre(\b)/g, '$1Glass pane$2'],
    [/(\b)aluminium(\b)/g, '$1aluminium$2'],
    [/(\b)Aluminium(\b)/g, '$1Aluminium$2'],

    // finishes
    [/(\b)noir brillant(\b)/g, '$1glossy black$2'],
    [/(\b)Noir brillant(\b)/g, '$1Glossy black$2'],
    [/(\b)peinture piano(\b)/g, '$1piano paint$2'],
    [/(\b)Peinture piano(\b)/g, '$1Piano paint$2'],
    [/(\b)laque piano(\b)/g, '$1piano lacquer$2'],
    [/(\b)Laque piano(\b)/g, '$1Piano lacquer$2'],
    [/(\b)finition piano(\b)/g, '$1piano finish$2'],
    [/(\b)Finition piano(\b)/g, '$1Piano finish$2'],
    [/(\b)vernis(\b)/g, '$1varnish$2'],
    [/(\b)Vernis(\b)/g, '$1Varnish$2'],

    // security
    [/(\b)serrure double alarme(\b)/g, '$1dual-alarm lock$2'],
    [/(\b)Serrure double alarme(\b)/g, '$1Dual-alarm lock$2'],
    [/(\b)déverrouillage par empreinte digitale(\b)/g, '$1fingerprint unlocking$2'],
    [/(\b)Déverrouillage par empreinte digitale(\b)/g, '$1Fingerprint unlocking$2'],
    [/(\b)empreinte digitale(\b)/g, '$1fingerprint$2'],
    [/(\b)biométrique(\b)/g, '$1biometric$2'],
    [/(\b)Biométrique(\b)/g, '$1Biometric$2'],
    [/(\b)serrure à clé(\b)/g, '$1key lock$2'],
    [/(\b)Serrure à clé(\b)/g, '$1Key lock$2'],
    [/(\b)serrure(\b)/g, '$1lock$2'],
    [/(\b)Serrure(\b)/g, '$1Lock$2'],

    // lighting
    [/(\b)éclairage LED(\b)/g, '$1LED lighting$2'],
    [/(\b)Éclairage LED(\b)/g, '$1LED lighting$2'],
    [/(\b)lumière LED(\b)/g, '$1LED light$2'],
    [/(\b)Lumière LED(\b)/g, '$1LED light$2'],
    [/(\b)éclairage(\b)/g, '$1lighting$2'],
    [/(\b)Éclairage(\b)/g, '$1Lighting$2'],

    // dimensions vocab
    [/(\b)largeur(\b)/g, '$1width$2'],
    [/(\b)Largeur(\b)/g, '$1Width$2'],
    [/(\b)hauteur(\b)/g, '$1height$2'],
    [/(\b)Hauteur(\b)/g, '$1Height$2'],
    [/(\b)profondeur(\b)/g, '$1depth$2'],
    [/(\b)Profondeur(\b)/g, '$1Depth$2'],

    // box contents
    [/(\b)guide d'utilisation(\b)/g, '$1user guide$2'],
    [/(\b)Guide d'utilisation(\b)/g, '$1User guide$2'],
    [/(\b)guide d'entretien(\b)/g, '$1care guide$2'],
    [/(\b)Guide d'entretien(\b)/g, '$1Care guide$2'],
    [/(\b)certificat(\b)/g, '$1certificate$2'],
    [/(\b)Certificat(\b)/g, '$1Certificate$2'],
    [/(\b)guide(\b)/g, '$1guide$2'],
    [/(\b)Guide(\b)/g, '$1Guide$2'],

    // numerics / units
    [/tours par jour/g, 'turns per day'],
    [/tours\/jour/g, 'turns/day'],
    [/rotations par jour/g, 'turns per day'],
    [/rotations\/jour/g, 'turns/day'],
    [/(\b)tour(\b)/g, '$1turn$2'],
    [/(\b)tours(\b)/g, '$1turns$2'],

    // structure / connectors (last)
    [/(\b)dont(\b)/g, '$1of which$2'],
    [/(\b)inclus(\b)/g, '$1included$2'],
    [/(\b)Inclus(\b)/g, '$1Included$2'],

    // ── French residue I missed in the first pass ──
    [/(\b)en remontoir(s)?(\b)/g, '$1on the winder$3'],
    [/(\b)en rotation(\b)/g, '$1in rotation$2'],
    [/(\b)en stockage(\b)/g, '$1in storage$2'],
    [/(\b)en remontage(\b)/g, '$1winding$2'],
    [/(\b)en rangement(\b)/g, '$1in storage$2'],
    [/(\b)de remontage(\b)/g, '$1for winding$2'],
    [/(\b)de rangement(\b)/g, '$1for storage$2'],
    [/(\b)de stockage(\b)/g, '$1for storage$2'],
    [/(\b)de présentation(\b)/g, '$1for display$2'],
    [/(\b)pour montres? automatiques?\b/gi, ' for automatic watches'],
    [/(\b)pour montres?\b/gi, ' for watches'],
    [/(\b)pour montre\b/gi, ' for watches'],
    [/(\d+)\s+emplacements? de montres?/gi, (m, n) => `${n} watch slot${parseInt(n,10)===1?'':'s'}`],
    [/(\d+)\s+emplacements? remontoirs?/gi, (m, n) => `${n} winder slot${parseInt(n,10)===1?'':'s'}`],
    [/(\d+)\s+emplacements?\s+supplémentaires?/gi, (m, n) => `${n} extra slot${parseInt(n,10)===1?'':'s'}`],
    [/(\b)emplacements? rotatifs?\b/g, '$1rotating slot$2'.replace('$2','s')], // simplest: always plural-safe
    [/(\b)emplacements? dédiés?\b/g, '$1dedicated slots'],
    [/(\b)emplacement\s+dédié\b/g, '$1dedicated slot'],
    [/(\b)emplacement\s+unique\b/g, '$1single slot'],
    [/(\b)emplacement(\b)/g, '$1slot$2'],
    [/(\b)emplacements(\b)/g, '$1slots$2'],
    [/(\b)dédiée?s?\b/gi, '$1dedicated'],
    [/(\b)indépendants?\b/gi, '$1independent'],
    [/(\b)indépendantes?\b/gi, '$1independent'],
    [/(\b)supplémentaires?\b/gi, '$1extra'],
    [/(\b)motorisées?\b/gi, '$1motorised'],
    [/(\b)motorisés?\b/gi, '$1motorised'],
    [/(\b)logements?\b/gi, '$1compartment'],
    [/(\b)tiroirs?\b/gi, '$1drawer'],
    [/(\b)dans (\w+) logements?\b/gi, '$1in $2 compartments'],
    [/(\b)rangements?\b/gi, '$1storage'],
    [/(\b)rangées\b/gi, '$1stored'],
    [/(\b)rotatifs?\b/gi, '$1rotating'],
    [/(\b)supports?\b/gi, '$1mount'],
    [/(\b)deux\b/gi, '$1two'],
    [/(\b)Deux\b/g, '$1Two'],
    [/(\b)trois\b/gi, '$1three'],
    [/(\b)Trois\b/g, '$1Three'],
    [/(\b)quatre\b/gi, '$1four'],
    [/(\b)Quatre\b/g, '$1Four'],
    [/(\b)cinq\b/gi, '$1five'],
    [/(\b)Cinq\b/g, '$1Five'],
    [/(\b)six\b/gi, '$1six'],
    [/(\b)sept\b/gi, '$1seven'],
    [/(\b)huit\b/gi, '$1eight'],
    [/(\b)neuf\b/gi, '$1nine'],
    [/(\b)dix\b/gi, '$1ten'],
    [/(\b)douze\b/gi, '$1twelve'],

    // numeric ranges / connectors (phrases BEFORE single-word fallbacks)
    [/(\d+)\s+à\s+(\d+)/g, '$1 to $2'],
    [/(\d+)\s+ou\s+(\d+)/g, '$1 or $2'],

    // dimension prose
    [/(\b)de hauteur\b/gi, '$1tall'],
    [/(\b)de haut\b/gi, '$1tall'],
    [/(\b)de largeur\b/gi, '$1wide'],
    [/(\b)de profondeur\b/gi, '$1deep'],
    [/(\b)de diamètre\b/gi, '$1diameter'],
    [/(\b)de poids\b/gi, '$1weight'],

    // misc phrase connectors (must run BEFORE bare un/une rules)
    [/pour une bonne stabilité/gi, 'for good stability'],
    [/pour une utilisation quotidienne/gi, 'for daily use'],
    [/pour la stabilité/gi, 'for stability'],
    [/au choix/gi, 'selectable'],
    [/(\b)arrêt\b/gi, '$1stop'],
    [/(\b)Arrêt\b/g, '$1Stop'],

    // bare un/une fallback (last so phrases above already absorbed common cases)
    [/\bun\b/g, 'one'],
    [/\bune\b/g, 'one'],
    [/\bUne?\b/g, 'One'],
    [/(\b)tours par jour\b/gi, '$1turns per day'],
    [/(\b)tours\/jour\b/gi, '$1turns/day'],
    [/(\b)rotations\/jour\b/gi, '$1turns/day'],
    [/(\b)d['’]expositions?\b/gi, '$1display'],
    [/(\b)expositions?\b/gi, '$1display'],
    [/(\b)dans une?\b/gi, '$1in a'],
    [/(\b)dans la\b/gi, '$1in the'],
    [/(\b)dans le\b/gi, '$1in the'],
    [/(\b)couleur(\b)/g, '$1colour$2'],
    [/(\b)Couleur(\b)/g, '$1Colour$2'],
    [/(\b)marron(\b)/g, '$1brown$2'],
    [/(\b)Marron(\b)/g, '$1Brown$2'],
    [/(\b)blanc(\b)/g, '$1white$2'],
    [/(\b)Blanc(\b)/g, '$1White$2'],
    [/(\b)noir(\b)/g, '$1black$2'],
    [/(\b)Noir(\b)/g, '$1Black$2'],
    [/(\b)gris(\b)/g, '$1grey$2'],
    [/(\b)Gris(\b)/g, '$1Grey$2'],
    [/(\b)beige(\b)/g, '$1beige$2'],
    [/(\b)rouge(\b)/g, '$1red$2'],
    [/(\b)Rouge(\b)/g, '$1Red$2'],
    [/(\b)jaune(\b)/g, '$1yellow$2'],
    [/(\b)Jaune(\b)/g, '$1Yellow$2'],
    [/(\b)bleu(\b)/g, '$1blue$2'],
    [/(\b)Bleu(\b)/g, '$1Blue$2'],
    [/(\b)vert(\b)/g, '$1green$2'],
    [/(\b)Vert(\b)/g, '$1Green$2'],
    [/(\b)style(\b)/g, '$1style$2'],
    [/(\b)Style(\b)/g, '$1Style$2'],
    [/(\b)avec(\b)/g, '$1with$2'],
    [/(\b)Avec(\b)/g, '$1With$2'],
    [/(\b)sans(\b)/g, '$1without$2'],
    [/(\b)Sans(\b)/g, '$1Without$2'],
    [/(\b)pour(\b)/g, '$1for$2'],
    [/(\b)Pour(\b)/g, '$1For$2'],

    // ── EU → US wording (the EN locale is US-targeted) ──
    [/adaptateur EU\/US\/UK inclus/g, 'US/EU/UK adapter included'],
    [/Adaptateur EU\/US\/UK inclus/g, 'US/EU/UK adapter included'],
    [/EU\/UK\/US\/CA adapters included/g, 'US/EU/UK/CA adapters included'],
    [/EU adapter included/g, 'US adapter included'],
    [/Adaptateur EU inclus/g, 'US adapter included'],
    [/adaptateur EU inclus/g, 'US adapter included'],
    [/adaptateur secteur EU/g, 'US mains adapter'],
    [/Adaptateur secteur EU/g, 'US mains adapter'],
    [/adaptateur EU/g, 'US adapter'],
    [/Adaptateur EU/g, 'US adapter'],
    [/(\b)câble EU(\b)/g, '$1US cable$2'],
    [/(\b)Câble EU(\b)/g, '$1US cable$2'],
    [/(\b)câble US(\b)/g, '$1US cable$2'],

    // ── Power source extras ──
    [/compartiment piles externe/gi, 'external battery compartment'],
    [/compartiment piles interne/gi, 'internal battery compartment'],
    [/compartiment externe pour piles/gi, 'external battery compartment'],
    [/compartiment interne pour piles/gi, 'internal battery compartment'],
    [/emplacement pour 2 batteries/gi, 'slot for 2 batteries'],
    [/emplacement pour batterie/gi, 'battery slot'],
    [/(\d+)\s*piles?\s*D\b/gi, (m, n) => `${n} D-cell batter${parseInt(n,10)===1?'y':'ies'}`],
    [/(\d+)\s*piles?\s*AA\b/gi, (m, n) => `${n} AA batter${parseInt(n,10)===1?'y':'ies'}`],
    [/(\d+)\s*piles?\s*AAA\b/gi, (m, n) => `${n} AAA batter${parseInt(n,10)===1?'y':'ies'}`],
    [/(\b)piles\b/g, '$1batteries'],
    [/(\b)pile\b/g, '$1battery'],
    [/(\b)Piles\b/g, '$1Batteries'],
    [/(\b)Pile\b/g, '$1Battery'],
    [/option sur piles/gi, 'battery option'],
    [/option piles/gi, 'battery option'],
    [/sur piles/gi, 'on batteries'],
    [/par piles/gi, 'by batteries'],
    [/AC\/DC et /g, 'AC/DC and '],
    [/(\b)consommation d['’]énergie réduite/gi, '$1reduced power consumption'],
    [/(\b)faible dépense d['’]énergie/gi, '$1low energy use'],
    [/notice/gi, 'manual'],
    [/Notice/gi, 'Manual'],
    [/(\b)câble fourni(e?)/gi, '$1cable included'],
    [/(\b)câble et notice fournis(e?)?/gi, '$1cable and manual included'],
    [/(\b)câble et notice/gi, '$1cable and manual'],
    [/(\b)5 modes de rotation/gi, '$15 rotation modes'],
    [/écran tactile et télécommande/gi, 'touchscreen and remote control'],
    [/écran tactile/gi, 'touchscreen'],
    [/Écran tactile/gi, 'Touchscreen'],
    [/éclairage LED intégré/gi, 'built-in LED lighting'],
    [/possibles?/gi, 'available'],
    [/(\b)ou batterie(\b)/gi, '$1or battery$2'],

    // ── Final mop-up using Unicode-aware boundaries (\b breaks on é/à/etc.) ──
    // These run AFTER all the targeted phrase rules so we only touch leftover
    // bare French words that the earlier passes didn't already absorb.
    [ACCi('coussins?'), 'cushion'],
    [ACCi('remontoirs?'), 'winder'],
    [ACCi('automatiques?'), 'automatic'],
    [ACCi('montres?'), 'watch'],
    [ACCi('sens'), 'directions'],
    [ACCi('dont'), 'of which'],
    [ACCi('alterné'), 'alternating'],
    [ACCi('alternée'), 'alternating'],
    [ACCi('alternés'), 'alternating'],
    [ACCi('alternées'), 'alternating'],
    [ACCi('combinée?s?'), 'combined'],
    [ACCi('mixte'), 'mixed'],
    [ACCi('réglable'), 'adjustable'],
    [ACCi('incluse?s?'), 'included'],
    [ACCi('avec'), 'with'],
    [ACCi('sans'), 'without'],
    [ACCi('réf'), 'ref'],
    [ACCi('génération'), 'generation'],
    [ACCi('fonctionnement'), 'operation'],
    [ACCi('fourni'), 'included'],
    [ACCi('intérieure?s?'), 'interior'],
    [ACCi('laquée?s?'), 'lacquered'],
    [ACCi('laque'), 'lacquer'],
    [ACCi('télécommande'), 'remote control'],
    [ACCi('silencieux'), 'silent'],
    [ACCi('silencieuse'), 'silent'],
    [ACCi('antimagnétisme'), 'anti-magnetism'],
    [ACCi('optimisée?s?'), 'optimised'],
    [ACCi('optimisés?'), 'optimised'],
    [ACCi('composée?s?'), 'composite'],
    [ACCi('composés?'), 'composite'],
    [ACCi('crème'), 'cream'],
    [ACCi('Crème'), 'Cream'],
    [ACCi('vitesse réglée'), 'speed set'],
    [ACCi('réglée?'), 'set'],
    [ACCi('câble d[\'’]alimentation'), 'power cable'],
    [ACCi('Câble d[\'’]alimentation'), 'Power cable'],
    [ACCi('alimentation USB-C'), 'USB-C power'],
    [ACCi('alimentation USB'), 'USB power'],
    [ACCi('alimentation'), 'power supply'],
    [ACCi('Alimentation'), 'Power supply'],
    [ACCi('adaptateurs'), 'adapters'],
    [ACCi('Adaptateurs'), 'Adapters'],
    [ACCi('adaptateur'), 'adapter'],
    [ACCi('Adaptateur'), 'Adapter'],
    [/(\d+)ème/g, (m, n) => {
      const map = { '1': '1st', '2': '2nd', '3': '3rd' };
      return map[n] || (n + 'th');
    }],
    [/Marlboro de (\d+)(?:st|nd|rd|th) generation/g, 'Marlboro $1 generation'],
    [ACCi('finition peinture mate'), 'matte paint finish'],
    [ACCi('finition peinture piano'), 'piano paint finish'],
    [ACCi('finition piano'), 'piano finish'],
    [ACCi('finition mate'), 'matte finish'],
    [ACCi('finition'), 'finish'],
    [ACCi('peinture piano'), 'piano paint'],
    [ACCi('peinture mate'), 'matte paint'],
    [ACCi('peinture mat'), 'matte paint'],
    [ACCi('peinture'), 'paint'],
    [ACCi('intégrée?s?'), 'built-in'],
    [ACCi('intégr'), 'built-in'],
    [ACCi('dédiée?s?'), 'dedicated'],
    [ACCi('dédié'), 'dedicated'],
    [ACCi('dorée?s?'), 'gold'],
    [ACCi('argentée?s?'), 'silver'],
    [ACCi('coloris'), 'colour'],
    [ACCi('intérieurs?'), 'interior'],
    [ACCi('extérieurs?'), 'exterior'],
    [ACCi('boîtiers?'), 'case'],
    [ACCi('métal'), 'metal'],
    [ACCi('métallique'), 'metal'],
    [ACCi('hêtre'), 'beech'],
    [ACCi('céruse'), 'cerused'],
    [ACCi('mémoire'), 'memory'],
    [ACCi('matériaux?'), 'materials'],
    [ACCi('matière'), 'material'],
    [ACCi('matières'), 'materials'],
    [ACCi('synthétiques?'), 'synthetic'],
    [ACCi('arrière'), 'rear'],
    [ACCi('avant'), 'front'],
    [ACCi('brillante?s?'), 'glossy'],
    [ACCi('matte?s?'), 'matte'],
    [ACCi('inox'), 'stainless'],
    [ACCi('inoxydable'), 'stainless'],
    [ACCi('aluminium'), 'aluminium'],
    [ACCi('acrylique'), 'acrylic'],
    [ACCi('rosée?'), 'pink'],
    [ACCi('cuivrée?'), 'copper'],
    [ACCi('alliée?'), 'alloy'],
    [ACCi('rayée?'), 'striped'],
    [ACCi('vermillon'), 'vermilion'],
    [ACCi('moteur'), 'motor'],
    [ACCi('réduite?'), 'reduced'],
    [ACCi('élégante?s?'), 'elegant'],
    [ACCi('fournies?'), 'included'],
    [ACCi('fournis'), 'included'],
    [ACCi('câble'), 'cable'],
    [ACCi('Câble'), 'Cable'],

    // ── Compound phrase residues (compound first, single-word below) ──
    [ACCi('coffret en bois'), 'wooden box'],
    [ACCi('peau d[\'’]autruche'), 'ostrich leather'],
    [ACCi('molleton perlé'), 'pearl fleece'],
    [ACCi('peinture mate'), 'matte paint'],
    [ACCi('finition mate'), 'matte finish'],
    [ACCi('plaqué piano'), 'piano-plated'],
    [ACCi('finition plaqué piano'), 'piano-plated finish'],
    [ACCi('cuir skaï'), 'synthetic leather'],
    [ACCi('simili cuir'), 'faux leather'],
    [ACCi('imprimé crocodile'), 'crocodile print'],
    [ACCi('esprit malachite'), 'malachite-inspired'],
    [ACCi('vert émeraude'), 'emerald green'],
    [ACCi('rose dragée'), 'sugared pink'],
    [ACCi('marron châtaignier'), 'chestnut brown'],
    [ACCi('marron châtaigne'), 'chestnut brown'],
    [ACCi('aluminium granulé'), 'granulated aluminium'],
    [ACCi('câble d[\'’]alimentation EU'), 'US power cable'],
    [ACCi('câble d[\'’]alimentation US'), 'US power cable'],

    // ── Accented colour / material singletons (must use ACCi: \b can't anchor on é/ï/è) ──
    [ACCi('émeraude'), 'emerald'],
    [ACCi('Émeraude'), 'Emerald'],
    [ACCi('céruléan'), 'cerulean'],
    [ACCi('Céruléan'), 'Cerulean'],
    [ACCi('ébène'), 'ebony'],
    [ACCi('Ébène'), 'Ebony'],
    [ACCi('châtaignier'), 'chestnut'],
    [ACCi('châtaigne'), 'chestnut'],
    [ACCi('albâtre'), 'alabaster'],
    [ACCi('Albâtre'), 'Alabaster'],
    [ACCi('granulé'), 'granulated'],
    [ACCi('plaquée?'), 'plated'],
    [ACCi('skaï'), 'synthetic leather'],
    [ACCi('Skaï'), 'Synthetic leather'],
    [ACCi('kaki'), 'khaki'],
    [ACCi('café'), 'coffee'],
    [ACCi('Café'), 'Coffee'],
    [ACCi('imprimée?s?'), 'print'],
    [ACCi('annoncée?s?'), 'rated'],
    [ACCi('noires?'), 'black'],
    [ACCi('Noires?'), 'Black'],
    [ACCi('blanche?s?'), 'white'],
    [ACCi('Blanche?s?'), 'White'],
    [ACCi('grises?'), 'grey'],
    [ACCi('Grises?'), 'Grey'],
    [ACCi('verte?s?'), 'green'],
    [ACCi('Verte?s?'), 'Green'],

    // éclairage / écran — \b breaks on the leading é, so ACCi is mandatory
    [ACCi('éclairage LED'), 'LED lighting'],
    [ACCi('Éclairage LED'), 'LED lighting'],
    [ACCi('écran LCD'), 'LCD screen'],
    [ACCi('Écran LCD'), 'LCD screen'],
    [ACCi('écran tactile'), 'touchscreen'],
    [ACCi('Écran tactile'), 'Touchscreen'],
    [ACCi('éclairage'), 'lighting'],
    [ACCi('Éclairage'), 'Lighting'],
    [ACCi('écran'), 'screen'],
    [ACCi('Écran'), 'Screen'],

    // FR connectors — only safe AFTER all multi-word rules above have run
    [ACCi('et'), 'and'],
    [ACCi('ou'), 'or'],
    [ACCi('sur'), 'on'],
    [ACCi('sous'), 'under'],
    [ACCi('dans'), 'in'],
    [ACCi('par'), 'per'],
    [ACCi('de'), 'of'],
    [ACCi('du'), 'of the'],
    [ACCi('des'), 'of'],
    [ACCi('le'), 'the'],
    [ACCi('la'), 'the'],
    [ACCi('les'), 'the'],
    [ACCi('aux'), 'to the'],
    [ACCi('au'), 'at the'],
    [ACCi('à'), 'to'],
    // Possessive
    [ACCi('votre'), 'your'],
    [ACCi('vos'), 'your'],
    [ACCi('mon'), 'my'],
    [ACCi('ma'), 'my'],
    [ACCi('mes'), 'my'],

    // ── Unit conversion: metric → US imperial for the EN locale ──
    // Comma-decimal first so the digit-to-unit regex sees clean numbers.
    [/(\d),(\d)/g, '$1.$2'],
    // Multi-axis dimensions ("13 × 13 × 17 cm" / "12x12x12 cm") → convert all numbers
    [/(\d+(?:\.\d+)?(?:\s*[×x]\s*\d+(?:\.\d+)?){1,3})\s*cm\b/g, (m, group) => {
      const conv = group.replace(/(\d+(?:\.\d+)?)/g, (n) => {
        return (parseFloat(n) * 0.3937).toFixed(1).replace(/\.0$/, '');
      });
      return conv + ' in';
    }],
    // Single value cm → in
    [/(\d+(?:\.\d+)?)\s*cm\b/g, (m, n) => {
      const inches = (parseFloat(n) * 0.3937).toFixed(1).replace(/\.0$/, '');
      return inches + ' in';
    }],
    // Millimetres → inches
    [/(\d+(?:\.\d+)?)\s*mm\b/g, (m, n) => {
      const inches = (parseFloat(n) * 0.03937).toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
      return inches + ' in';
    }],
    // Kilograms → pounds (1 kg = 2.205 lb)
    [/(\d+(?:\.\d+)?)\s*kg\b/g, (m, n) => {
      const lbs = (parseFloat(n) * 2.205).toFixed(1).replace(/\.0$/, '');
      return lbs + ' lb';
    }],
    // Grammes → ounces. Only convert ≥ 200 g to avoid micro values.
    [/(\d{3,})\s*g\b/g, (m, n) => {
      const grams = parseFloat(n);
      if (grams < 200) return m;
      const oz = (grams * 0.0353).toFixed(1).replace(/\.0$/, '');
      return oz + ' oz';
    }],
  ];

  const translate = (s) => {
    if (!s) return s;
    let out = s;
    for (const [pat, rep] of REPLACEMENTS) {
      out = out.replace(pat, rep);
    }
    return out;
  };

  const walk = (el) => {
    const tw = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    const texts = [];
    let n;
    while ((n = tw.nextNode())) texts.push(n);
    for (const node of texts) {
      const before = node.nodeValue;
      const after = translate(before);
      if (after !== before) node.nodeValue = after;
    }
  };

  const run = () => {
    document.querySelectorAll('[data-vln-specs-translate][data-locale="en"]').forEach(walk);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
